import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import { KpiVariable } from 'types/GlobalKpiVariable'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

// Fetches the sort when indices has not been set yet
function getInitialSort(variables: KpiVariable[]) {
  const levelZero = variables
    .filter((v) => v.level === 0)
    .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
  const levelOne = variables
    .filter((v) => v.level === 1)
    .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
  const levelTwo = variables
    .filter((v) => v.level === 2)
    .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))

  const initialSort: KpiVariable[] = []

  levelZero.forEach((v0) => {
    initialSort.push(v0)

    const levelOneChildren = levelOne.filter(
      (v1) => v1.parent_kpi_variable_id === v0.id
    )

    levelOneChildren.forEach((v1) => {
      initialSort.push(v1)

      const levelTwoChildren = levelTwo.filter(
        (v2) => v2.parent_kpi_variable_id === v1.id
      )

      initialSort.push(...levelTwoChildren)
    })
  })

  return initialSort
}

export const selectShouldFetch = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.KpiVariableStore.fetched,
    (state: ApplicationState) => state.KpiVariableStore.fetching
  ],
  (fetched, fetching) => !fetched && !fetching
)

// sorted variables, loading
export const selectKpiVariables = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.KpiVariableStore.data,
    (state: ApplicationState) => state.KpiVariableStore.fetched
  ],
  (data, fetched): [KpiVariable[], boolean] => {
    if (!fetched) {
      return [[], true]
    }

    const variables = Object.values(data)

    if (variables.length === 0) {
      return [[], false]
    }

    // Indices has been set
    if (variables[0].index) {
      return [
        variables.sort((a, b) => (a.index as number) - (b.index as number)),
        false
      ]
    }

    return [getInitialSort(variables), false]
  }
)
